import React, { useCallback, useEffect, useState } from "react";
import { Brain, Play, Plus, Save, Trash2, Zap } from "lucide-react";
import { toast } from "sonner";
import { whatsappMerkez } from "@/lib/api";
import { Button } from "@/components/ui/button";

const KOSUL_SABLONLARI = [
    {
        id: "tahkim-16",
        label: "16 gün — Tahkim geçişi",
        kosul: {
            dosya_durumlari: ["BASVURU", "YENI"],
            min_kayit_gun: 16,
            tahkim_mesaj_gonderildi: false,
            sigorta_cevap_yok: true,
        },
        aksiyon: { yeni_durum: "TAHKIM", takvim: true },
    },
    {
        id: "evrak-eksik",
        label: "Eksik evrak hatırlatma",
        kosul: { dosya_durumlari: ["YENI", "BASVURU"], evrak_eksik: true },
        aksiyon: { sablon_kod: "evrak_hatirlatma" },
        sablon_kod: "evrak_hatirlatma",
    },
];

export default function WhatsAppKurallarPanel({ runBusy }) {
    const [kurallar, setKurallar] = useState([]);
    const [loading, setLoading] = useState(true);
    const [form, setForm] = useState({
        kod: "",
        ad: "",
        aciklama: "",
        oncelik: 100,
        sablon_kod: "",
        kosul_json: "{}",
        aksiyon_json: "{}",
    });

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const data = await whatsappMerkez.kurallar();
            setKurallar(data.kurallar || []);
        } catch {
            toast.error("Kurallar yüklenemedi.");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const applySablon = (s) => {
        setForm((p) => ({
            ...p,
            ad: s.label,
            kod: p.kod || s.id,
            kosul_json: JSON.stringify(s.kosul, null, 2),
            aksiyon_json: JSON.stringify(s.aksiyon, null, 2),
            sablon_kod: s.sablon_kod || s.aksiyon?.sablon_kod || "",
        }));
    };

    const handleKaydet = async () => {
        let kosul, aksiyon;
        try {
            kosul = JSON.parse(form.kosul_json || "{}");
            aksiyon = JSON.parse(form.aksiyon_json || "{}");
        } catch {
            toast.error("Koşul veya aksiyon JSON geçersiz.");
            return;
        }
        await runBusy("kural-kaydet", async () => {
            await whatsappMerkez.kuralOlustur({
                kod: form.kod.trim(),
                ad: form.ad.trim(),
                aciklama: form.aciklama,
                oncelik: Number(form.oncelik) || 100,
                kosul,
                aksiyon,
                sablon_kod: form.sablon_kod,
                aktif: true,
            });
            toast.success("Kural oluşturuldu.");
            setForm({
                kod: "",
                ad: "",
                aciklama: "",
                oncelik: 100,
                sablon_kod: "",
                kosul_json: "{}",
                aksiyon_json: "{}",
            });
            await load();
        });
    };

    const toggleAktif = async (k) => {
        await runBusy(`kural-${k.id}`, async () => {
            await whatsappMerkez.kuralGuncelle(k.id, { aktif: !k.aktif });
            await load();
        });
    };

    const handleSil = async (k) => {
        if (!window.confirm(`"${k.ad}" silinsin mi?`)) return;
        await runBusy(`kural-sil-${k.id}`, async () => {
            await whatsappMerkez.kuralSil(k.id);
            toast.success("Silindi.");
            await load();
        });
    };

    const handleCalistir = async () => {
        await runBusy("kurallar-calistir", async () => {
            const stats = await whatsappMerkez.kurallarCalistir();
            toast.success(
                `Kurallar çalıştı: ${stats.uygulanan || 0} aksiyon, ${stats.eslesen || 0} eşleşme`
            );
        });
    };

    return (
        <div className="space-y-6 max-w-4xl">
            <section className="rounded-xl border border-[rgba(147,112,219,0.35)] bg-gradient-to-br from-[#1a1033]/60 via-[#0b1437]/90 to-[#111c4a]/80 p-5">
                <div className="flex flex-wrap items-start justify-between gap-4 mb-4">
                    <div>
                        <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-violet-300 flex items-center gap-1.5">
                            <Brain className="h-3.5 w-3.5" />
                            No-Code Otomasyon Kuralları
                        </p>
                        <h3 className="text-base font-display text-[#f5ecd6] mt-1">
                            Koşul → aksiyon — panelden yönetin
                        </h3>
                        <p className="text-xs text-[#8b93ae] mt-1 max-w-xl">
                            Örn: Başvuru + 16 gün + sigorta cevap yok → otomatik TAHKIM + WhatsApp.
                            Günlük cron ile birlikte çalışır.
                        </p>
                    </div>
                    <Button
                        size="sm"
                        onClick={handleCalistir}
                        className="bg-violet-600/80 hover:bg-violet-600 text-white text-xs"
                    >
                        <Play className="h-3.5 w-3.5 mr-1" />
                        Şimdi çalıştır
                    </Button>
                </div>

                <div className="flex flex-wrap gap-2 mb-4">
                    {KOSUL_SABLONLARI.map((s) => (
                        <button
                            key={s.id}
                            type="button"
                            onClick={() => applySablon(s)}
                            className="text-[10px] px-2.5 py-1 rounded-full border border-violet-500/40 text-violet-200 hover:bg-violet-500/15"
                        >
                            + {s.label}
                        </button>
                    ))}
                </div>

                <div className="grid gap-3 sm:grid-cols-2">
                    <input
                        placeholder="Kural kodu — tahkim-16-gun"
                        value={form.kod}
                        onChange={(e) => setForm((p) => ({ ...p, kod: e.target.value }))}
                        className="rounded-md bg-[#111c4a] border border-white/10 px-3 py-2 text-sm"
                    />
                    <input
                        placeholder="Kural adı"
                        value={form.ad}
                        onChange={(e) => setForm((p) => ({ ...p, ad: e.target.value }))}
                        className="rounded-md bg-[#111c4a] border border-white/10 px-3 py-2 text-sm"
                    />
                    <input
                        placeholder="Şablon kodu (opsiyonel)"
                        value={form.sablon_kod}
                        onChange={(e) => setForm((p) => ({ ...p, sablon_kod: e.target.value }))}
                        className="rounded-md bg-[#111c4a] border border-white/10 px-3 py-2 text-sm"
                    />
                    <input
                        type="number"
                        placeholder="Öncelik (düşük = önce)"
                        value={form.oncelik}
                        onChange={(e) => setForm((p) => ({ ...p, oncelik: e.target.value }))}
                        className="rounded-md bg-[#111c4a] border border-white/10 px-3 py-2 text-sm"
                    />
                </div>
                <textarea
                    placeholder="Koşul JSON"
                    value={form.kosul_json}
                    onChange={(e) => setForm((p) => ({ ...p, kosul_json: e.target.value }))}
                    rows={4}
                    className="w-full mt-3 rounded-md bg-[#111c4a] border border-white/10 px-3 py-2 text-xs font-mono"
                />
                <textarea
                    placeholder='Aksiyon JSON — {"yeni_durum":"TAHKIM"} veya {"sablon_kod":"evrak_hatirlatma"}'
                    value={form.aksiyon_json}
                    onChange={(e) => setForm((p) => ({ ...p, aksiyon_json: e.target.value }))}
                    rows={3}
                    className="w-full mt-2 rounded-md bg-[#111c4a] border border-white/10 px-3 py-2 text-xs font-mono"
                />
                <Button onClick={handleKaydet} className="mt-3 bg-violet-600 hover:bg-violet-700 text-white text-xs">
                    <Plus className="h-3.5 w-3.5 mr-1" />
                    Kural ekle
                </Button>
            </section>

            <section className="rounded-xl border border-white/10 bg-[#0b1437]/80 p-5">
                <h3 className="text-sm font-semibold text-[#e6cf94] flex items-center gap-2 mb-3">
                    <Zap className="h-4 w-4" />
                    Aktif kurallar
                </h3>
                {loading ? (
                    <p className="text-xs text-[#8b93ae]">Yükleniyor…</p>
                ) : !kurallar.length ? (
                    <p className="text-xs text-[#8b93ae]">Henüz kural yok.</p>
                ) : (
                    <div className="space-y-2">
                        {kurallar.map((k) => (
                            <div
                                key={k.id}
                                className="rounded-lg border border-white/10 bg-black/20 p-3 flex flex-wrap items-start justify-between gap-2"
                            >
                                <div>
                                    <p className="text-sm text-[#f5ecd6] font-medium">{k.ad}</p>
                                    <p className="text-[10px] text-[#8b93ae]">
                                        {k.kod} · öncelik {k.oncelik}
                                        {k.son_calistirma
                                            ? ` · son: ${new Date(k.son_calistirma).toLocaleString("tr-TR")}`
                                            : ""}
                                    </p>
                                    <pre className="text-[9px] text-[#7dd3fc] mt-1 max-w-lg overflow-x-auto">
                                        {JSON.stringify(k.kosul)}
                                    </pre>
                                </div>
                                <div className="flex gap-2">
                                    <Button
                                        size="sm"
                                        variant="outline"
                                        className="h-7 text-[10px]"
                                        onClick={() => toggleAktif(k)}
                                    >
                                        {k.aktif ? "Aktif" : "Pasif"}
                                    </Button>
                                    <Button
                                        size="sm"
                                        variant="outline"
                                        className="h-7 text-[10px] text-rose-300"
                                        onClick={() => handleSil(k)}
                                    >
                                        <Trash2 className="h-3 w-3" />
                                    </Button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </section>
        </div>
    );
}
